import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { questions } from "./questions";
import { ProcessAutoResult } from "./ProcessAutoQuiz";
import { UserInfo } from "../../components/UserInfoForm";
import { webhookService } from "../../shared/webhookService";

interface ProcessAutoQuizContainerProps {
  userInfo: UserInfo | null;
}

const ProcessAutoQuizContainer: React.FC<ProcessAutoQuizContainerProps> = ({
  userInfo,
}) => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<ProcessAutoResult[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const handleOptionSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };

  const handleNext = async () => {
    if (selectedOption === null) return;

    const result: ProcessAutoResult = {
      questionId: questions[currentQuestion].id,
      selectedOption,
      scores: questions[currentQuestion].options[selectedOption].scores,
    };

    const newAnswers = [...answers, result];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(null);
    } else {
      // Store results and navigate
      localStorage.setItem("processauto_results", JSON.stringify(newAnswers));

      // Prepare webhook data
      const webhookData = {
        quizName: "Process Automation Quiz",
        userInfo: userInfo || {},
        responses: newAnswers.map((answer, index) => {
          const question = questions.find((q) => q.id === answer.questionId);
          return {
            questionNumber: index + 1,
            question: question?.text || "",
            answer: question?.options[answer.selectedOption]?.text || "",
          };
        }),
        timestamp: new Date().toISOString(),
      };

      // Send to webhook (with automatic retry on failure)
      await webhookService.submitQuizData(webhookData);

      navigate("/processauto/results");
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      const previousAnswer = answers[currentQuestion - 1];
      setSelectedOption(previousAnswer?.selectedOption ?? null);
      setAnswers(answers.slice(0, -1));
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Progress Bar */}
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5rem",
          }}
        >
          <span style={{ color: "white", fontSize: "0.9rem" }}>
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span style={{ color: "white", fontSize: "0.9rem" }}>
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div
          style={{
            width: "100%",
            height: "8px",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              backgroundColor: "#c9a750",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "2.5rem",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          maxWidth: "800px",
          width: "100%",
        }}
      >
        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            color: "#1a202c",
            marginBottom: "2rem",
            lineHeight: "1.4",
          }}
        >
          {questions[currentQuestion].text}
        </h2>

        {/* Options */}
        <div style={{ marginBottom: "2rem" }}>
          {questions[currentQuestion].options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionSelect(index)}
              style={{
                width: "100%",
                padding: "1.25rem",
                marginBottom: "1rem",
                fontSize: "1rem",
                textAlign: "left",
                backgroundColor: selectedOption === index ? "#fefce8" : "white",
                border:
                  selectedOption === index
                    ? "2px solid #c9a750"
                    : "2px solid #e2e8f0",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                color: "#1a202c",
                lineHeight: "1.6",
              }}
              onMouseEnter={(e) => {
                if (selectedOption !== index) {
                  e.currentTarget.style.borderColor = "#c9a750";
                  e.currentTarget.style.backgroundColor = "#fefce8";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedOption !== index) {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.backgroundColor = "white";
                }
              }}
            >
              {option.text}
            </button>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <button
            onClick={handleBack}
            disabled={currentQuestion === 0}
            style={{
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              fontWeight: "600",
              color: currentQuestion === 0 ? "#cbd5e0" : "#c9a750",
              backgroundColor: "white",
              border: `2px solid ${
                currentQuestion === 0 ? "#cbd5e0" : "#c9a750"
              }`,
              borderRadius: "8px",
              cursor: currentQuestion === 0 ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (currentQuestion !== 0) {
                e.currentTarget.style.backgroundColor = "#fefce8";
              }
            }}
            onMouseLeave={(e) => {
              if (currentQuestion !== 0) {
                e.currentTarget.style.backgroundColor = "white";
              }
            }}
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={selectedOption === null}
            style={{
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              fontWeight: "600",
              color: "white",
              backgroundColor: selectedOption === null ? "#cbd5e0" : "#c9a750",
              border: "none",
              borderRadius: "8px",
              cursor: selectedOption === null ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (selectedOption !== null) {
                e.currentTarget.style.backgroundColor = "#b8963d";
              }
            }}
            onMouseLeave={(e) => {
              if (selectedOption !== null) {
                e.currentTarget.style.backgroundColor = "#c9a750";
              }
            }}
          >
            {currentQuestion === questions.length - 1 ? "Submit" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessAutoQuizContainer;
